import {clamp, isNullOrZero, parseInteger} from '@benchling/js-utils';
import reactUpdate, {Spec} from 'immutability-helper';
import _ from 'underscore';

import {assert} from 'Asserts';
import * as EventConstants from 'constants/EventConstants';
import * as colors from 'lib/Colors';
import AppDispatcher, {FluxAction} from 'lib/Dispatcher';
import Store from 'lib/Store';
import {Protein, ProteinVersion, ProteinAnnotationVersion} from 'molbio/proteins/util/ProteinConstants';
import * as ProteinProperties from 'molbio/proteins/util/ProteinProperties';
import * as sequtil from 'molbio/SeqUtil';
import * as RegistryUtil from 'registry/RegistryUtil';
import * as VersionHistoryUtil from 'util/VersionHistoryUtil';

// Keep in sync with value in benchling/molbio/proteins/models/protein_annotation_version
export const MAX_ANNOTATION_NAME_LENGTH = 255;
// Keep in sync with value in benchling/molbio/proteins/models/size_limits
export const ANNOTATION_MAX_NOTES_LENGTH = 10000;

interface AnnotationEditorData {
  // pendingEditorFocus instructs the UI to put keyboard focus on the annotation editor.
  pendingEditorFocus: boolean;
  name: string;
  type: string;
  notes: string;
  displayStart: string;
  displayEnd: string;
  color: string;
  isInputValid?: boolean;
}

interface ProteinUIState {
  annotationEditorData: AnnotationEditorData | null;
  // pendingJump is used to instruct the UI to change the scroll position to make the given range
  // visible, then trigger a PROTEIN_PENDING_JUMP_COMPLETED action to mark it as done.
  pendingJump: {start: number; end: number} | null;
  hoveredAnnotationId: string | null;
  selectedAnnotationId: string | null;
  selectionStart: number;
  selectionEnd: number | null;
}

export interface SearchResult {
  index: number;
  aminoAcids: string;
  proteinId: string;
  isSelected?: boolean;
}

class ProteinStore extends Store {
  proteinsById!: {[proteinId: string]: Protein};
  uiStateByProteinId!: {[proteinId: string]: ProteinUIState};
  _versionsById!: {[proteinId: string]: Array<ProteinVersion>};
  _proteinIdsForSearchRefCount!: {[proteinId: string]: number};
  _pendingOpenSearchView!: boolean;
  searchQuery!: string | null;
  searchResults!: Array<SearchResult>;

  resetState(): void {
    this.proteinsById = {};
    this._versionsById = {};
    this._proteinIdsForSearchRefCount = {};
    this._pendingOpenSearchView = false;
    this.searchQuery = null;
    this.searchResults = [];
    this.uiStateByProteinId = {};
  }

  getProteinById(proteinId: string): Protein | undefined {
    return this.proteinsById[proteinId];
  }

  _setProtein(proteinId: string, protein: Protein): void {
    this.proteinsById[proteinId] = protein;
    if (this.uiStateByProteinId[proteinId] == null) {
      this.uiStateByProteinId[proteinId] = this._defaultUIState();
    }
  }

  _defaultUIState(): ProteinUIState {
    return {
      annotationEditorData: null,
      pendingJump: null,
      hoveredAnnotationId: null,
      selectedAnnotationId: null,
      selectionStart: 0,
      selectionEnd: null,
    };
  }

  _getUIState(proteinId: string): ProteinUIState {
    return this.uiStateByProteinId[proteinId] || this._defaultUIState();
  }

  getVersionsById(proteinId: string): Array<ProteinVersion> {
    return this._versionsById[proteinId];
  }

  getLatestVersionId(proteinId: string): number {
    if (!this._versionsById[proteinId]) {
      // If we haven't loaded the versions yet (which should be rare), just use the current version
      // as the latest version, since it's the latest that we currently know about.
      return this.proteinsById[proteinId].version.id;
    }
    let maxVersion = -1;
    for (const version of this._versionsById[proteinId]) {
      maxVersion = Math.max(maxVersion, version.id);
    }
    return maxVersion;
  }

  getHoveredAnnotationId(proteinId: string): ProteinUIState['hoveredAnnotationId'] {
    return this._getUIState(proteinId).hoveredAnnotationId;
  }

  getSelectedAnnotationId(proteinId: string): ProteinUIState['selectedAnnotationId'] {
    return this._getUIState(proteinId).selectedAnnotationId;
  }

  getSelection(proteinId: string): {start: number; end: number | null} {
    const uiState = this._getUIState(proteinId);
    return {
      start: uiState.selectionStart,
      end: uiState.selectionEnd,
    };
  }

  getAnnotationEditorData(proteinId: string): ProteinUIState['annotationEditorData'] {
    return this._getUIState(proteinId).annotationEditorData;
  }

  getPendingOpenSearchView(): boolean {
    return this._pendingOpenSearchView;
  }

  getPendingJump(proteinId: string): ProteinUIState['pendingJump'] {
    return this._getUIState(proteinId).pendingJump;
  }

  getAnnotation(proteinId: string, annotationId: string): ProteinAnnotationVersion | undefined {
    return _.findWhere(this.proteinsById[proteinId].version.annotations, {
      id: annotationId,
    });
  }

  getAnnotationAminoAcids(proteinId: string, annotationId: string): string {
    const annotation = this.getAnnotation(proteinId, annotationId)!;
    const fullAminoAcids = this.proteinsById[proteinId].version.aminoAcids;
    return sequtil.residueSlice(fullAminoAcids, annotation.start, annotation.end);
  }

  /**
   * Finds the version to revert to when doing an undo, or null if there is no previous version to
   * revert to. Generally, this is just a matter of walking one level up the tree, but if we are
   * already on a revert commit, then we are logically at our parent, so we should move to our
   * grandparent.
   */
  getUndoVersionId(proteinId: string): number | null {
    const currentVersion = this.proteinsById[proteinId].version;
    return VersionHistoryUtil.getVersionIdToResetTo(currentVersion, this._versionsById[proteinId]);
  }

  _setVersions(proteinId: string, versions: Array<ProteinVersion>): void {
    this._versionsById[proteinId] = versions;
  }

  getSearchResults(proteinId: string): Array<SearchResult> {
    return _.where(this.searchResults, {proteinId});
  }
  getNumSearchResults(): number {
    return this.searchResults.length;
  }
  getSelectedSearchResultIndex(): number {
    // eslint-disable-next-line no-restricted-syntax
    return _.findIndex(this.searchResults, ({isSelected}) => isSelected);
  }
  getSelectedSearchResult(): SearchResult | undefined {
    // eslint-disable-next-line no-restricted-syntax
    return _.find(this.searchResults, ({isSelected}) => isSelected);
  }

  _searchAminoAcids(proteinId: string, query: string): Array<SearchResult> {
    // Treat asterisks as stop codons, and Xs as arbitrary amino acids. This matches how we do
    // auto annotation, and should be kept in sync with benchling/lib/proteins::auto_annotate_protein. We
    // are nice and strip spaces since users may copy and paste from other places, which also matches how
    // we handle DNA sequences.
    const EXTRA_ALLOWED_CHARACTERS = 'X* ';
    const isValidQuery = query
      .split('')
      .every(
        (aminoAcid) =>
          ProteinProperties.areAminoAcidsValid(aminoAcid) || EXTRA_ALLOWED_CHARACTERS.includes(aminoAcid)
      );

    // If the query has invalid characters, just return zero results.
    if (!isValidQuery) {
      return [];
    }

    const regexQuery = query.replace(/\*/g, '$').replace(/X/gi, '.').replace(/ /g, '');

    const regex = new RegExp(`(?=(${regexQuery}))`, 'gi');
    const {aminoAcids} = this.getProteinById(proteinId)!.version;
    const results = [];
    let result = regex.exec(aminoAcids);
    while (result) {
      results.push({
        index: result.index,
        aminoAcids: result[1],
        proteinId,
      });
      regex.lastIndex = result.index + 1;
      result = regex.exec(aminoAcids);
    }
    return results;
  }

  addProteinsForSearch(proteinId: string): void {
    this._proteinIdsForSearchRefCount = reactUpdate(
      this._proteinIdsForSearchRefCount,
      _.object([[proteinId, {$apply: (count: number) => (count || 0) + 1}]])
    );
  }

  removeProteinsForSearch(proteinId: string): void {
    if (
      this._proteinIdsForSearchRefCount[proteinId] != null &&
      this._proteinIdsForSearchRefCount[proteinId] > 1
    ) {
      this._proteinIdsForSearchRefCount = reactUpdate(
        this._proteinIdsForSearchRefCount,
        _.object([
          [
            proteinId,
            {
              $apply(count: number) {
                return count - 1;
              },
            },
          ],
        ])
      );
    } else {
      // eslint-disable-next-line no-restricted-syntax
      this._proteinIdsForSearchRefCount = _.omit(this._proteinIdsForSearchRefCount, proteinId);
    }
    this.searchResults = this.searchResults.filter((result) => result.proteinId !== proteinId);
  }

  _getDisplaySelectionRange(
    proteinId: string,
    startIndex: number,
    endIndex: number | null
  ): {displayStart: string; displayEnd: string} {
    const protein = this.proteinsById[proteinId];
    const displayStartNumber = sequtil.modelStartToDisplay(startIndex, 1);
    const displayStartString = displayStartNumber == null ? '' : displayStartNumber.toString();
    const displayEndNumber = sequtil.modelEndToDisplay(endIndex, protein.version.aminoAcids.length, 1);
    const displayEndString = displayEndNumber == null ? '' : displayEndNumber.toString();
    return {displayStart: displayStartString, displayEnd: displayEndString};
  }

  _updateUiState(proteinId: string, updateObject: Spec<ProteinUIState>): void {
    this.uiStateByProteinId[proteinId] = reactUpdate(this._getUIState(proteinId), updateObject);
  }

  _updateAnnotationEditor(
    proteinId: string,
    updateObject: Spec<ProteinUIState['annotationEditorData']>
  ): void {
    this._updateUiState(proteinId, {
      annotationEditorData: updateObject,
    });
    this._recomputeAnnotationInputValidity(proteinId);
  }

  _recomputeAnnotationInputValidity(proteinId: string): void {
    const editorData = this.uiStateByProteinId[proteinId].annotationEditorData;
    if (editorData != null) {
      const isInputValid = this._isAnnotationInputValid(proteinId, editorData);
      this._updateUiState(proteinId, {annotationEditorData: {$merge: {isInputValid}}});
    }
  }

  _isAnnotationInputValid(proteinId: string, editorData: AnnotationEditorData): boolean {
    const protein = this.proteinsById[proteinId];
    const proteinLength = protein.version.aminoAcids.length;
    if (!editorData.name || editorData.name.length > MAX_ANNOTATION_NAME_LENGTH) {
      return false;
    }
    if (editorData.notes != null && editorData.notes.length > ANNOTATION_MAX_NOTES_LENGTH) {
      return false;
    }
    const displayStart = parseInteger(editorData.displayStart);
    const displayEnd = parseInteger(editorData.displayEnd);
    // eslint-disable-next-line benchlint/no-eqeqeq-null
    if (displayStart === null || isNullOrZero(displayEnd)) {
      return false;
    }
    const start = sequtil.displayStartToModel(displayStart, 1);
    const end = sequtil.displayEndToModel(displayEnd!, proteinLength, 1);
    return sequtil.isValidRange(start, end, proteinLength, false);
  }

  _deselectAnnotation(proteinId: string): void {
    this._updateUiState(proteinId, {
      $merge: {
        selectedAnnotationId: null,
        annotationEditorData: null,
      },
    });
  }

  /**
   * If the selection range is invalid (e.g. past the end of the range due to reverting to and older
   * protein version), make it valid.
   */
  _clampSelectionRange(proteinId: string): void {
    if (this.uiStateByProteinId[proteinId]) {
      const protein = this.proteinsById[proteinId];
      const clampIndex = (index: number | null): number | null =>
        index == null ? null : clamp(0, protein.version.aminoAcids.length, index);
      this._updateUiState(proteinId, {
        // @ts-expect-error Safe because number always maps to number
        selectionStart: {$apply: clampIndex},
        selectionEnd: {$apply: clampIndex},
      });
      const newUiState = this.uiStateByProteinId[proteinId];
      if (newUiState.selectionStart === newUiState.selectionEnd) {
        this._updateUiState(proteinId, {
          $merge: {
            selectionEnd: null,
          },
        });
      }
    }
  }

  _handleProteinUpdate(proteinId: string): void {
    this._clampSelectionRange(proteinId);
    // If the update got rid of the selected annotation, deselect it.
    const selectedAnnotationId = this.uiStateByProteinId[proteinId].selectedAnnotationId!;
    if (this.getAnnotation(proteinId, selectedAnnotationId) == null) {
      this._deselectAnnotation(proteinId);
    }
  }

  _recomputeSearchResults(): void {
    if (this.searchQuery == null) {
      this.searchResults = [];
      return;
    }
    const proteinIds = Object.keys(this._proteinIdsForSearchRefCount);
    // eslint-disable-next-line no-restricted-syntax
    const newSearchResults = _.flatten(
      proteinIds.map((proteinId) => this._searchAminoAcids(proteinId, this.searchQuery!))
    );
    if (newSearchResults.length > 0) {
      newSearchResults[0].isSelected = true;
    }
    this.searchResults = newSearchResults;
  }

  payloadHandler(action: FluxAction): boolean {
    switch (action.actionType) {
      case EventConstants.PROTEIN_OPEN_COMPLETED: {
        const {protein, proteinId} = action;
        assert(protein.id === proteinId, '');
        this._setProtein(proteinId, protein);
        this._handleProteinUpdate(proteinId);
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_MERGE: {
        const protein = this.getProteinById(action.proteinId)!;
        const newProtein = reactUpdate(protein, {$merge: action.protein});
        this._setProtein(action.proteinId, newProtein);
        this._handleProteinUpdate(action.proteinId);
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_UPDATE_COMPLETED: {
        const {newProtein, proteinId} = action;
        assert(newProtein.id === proteinId, '');
        this._setProtein(proteinId, newProtein);
        this._handleProteinUpdate(proteinId);
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_AA_UPDATE_COMPLETED: {
        const {newProtein, proteinId, newStart, newEnd} = action;
        assert(newProtein.id === proteinId, '');
        this._setProtein(proteinId, newProtein);
        this._updateUiState(proteinId, {$merge: {selectionStart: newStart, selectionEnd: newEnd}});
        this._handleProteinUpdate(proteinId);
        this._recomputeSearchResults();
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_FETCH_VERSIONS_COMPLETED: {
        const {proteinId, versions} = action;
        this._setVersions(proteinId, versions);
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_SEARCH_OPEN_VIEW: {
        this._pendingOpenSearchView = true;
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_SEARCH_OPEN_VIEW_COMPLETED: {
        this._pendingOpenSearchView = false;
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_SEARCH_AMINO_ACIDS: {
        this.searchQuery = action.query;
        this._recomputeSearchResults();
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_ADD_FOR_SEARCH: {
        for (let i = 0; i < action.proteinIds.length; i++) {
          const proteinId = action.proteinIds[i];
          this.addProteinsForSearch(proteinId);
        }
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_REMOVE_FROM_SEARCH: {
        for (let j = 0; j < action.proteinIds.length; j++) {
          const proteinId = action.proteinIds[j];
          this.removeProteinsForSearch(proteinId);
        }
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_VIEW_NEXT_SEARCH_RESULT: {
        const selectedIndex = this.getSelectedSearchResultIndex();
        if (selectedIndex < this.searchResults.length - 1 && selectedIndex !== -1) {
          this.searchResults = reactUpdate(
            this.searchResults,
            _.object([
              [selectedIndex, {$merge: {isSelected: false}}],
              [selectedIndex + 1, {$merge: {isSelected: true}}],
            ])
          );
        }
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_VIEW_PREVIOUS_SEARCH_RESULT: {
        const selectedIndex = this.getSelectedSearchResultIndex();
        if (selectedIndex > 0) {
          this.searchResults = reactUpdate(
            this.searchResults,
            _.object([
              [selectedIndex, {$merge: {isSelected: false}}],
              [selectedIndex - 1, {$merge: {isSelected: true}}],
            ])
          );
        }
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_SELECT_SEARCH_RESULT: {
        const selectedIndex = this.getSelectedSearchResultIndex();
        const targetIndex = this.searchResults.findIndex(
          ({proteinId, index, aminoAcids}) =>
            proteinId === action.proteinId && index === action.index && aminoAcids === action.aminoAcids
        );
        if (targetIndex !== -1 && targetIndex !== selectedIndex) {
          this.searchResults = reactUpdate(
            this.searchResults,
            _.object([
              [selectedIndex, {$merge: {isSelected: false}}],
              [targetIndex, {$merge: {isSelected: true}}],
            ])
          );
        }
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_CLEAR_SEARCH_RESULTS: {
        this.searchResults = [];
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_HOVER_ANNOTATION: {
        const {proteinId, proteinAnnotationId} = action;
        // Protein annotation id can be null
        this._updateUiState(proteinId, {$merge: {hoveredAnnotationId: proteinAnnotationId}});
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_UNHOVER_ANNOTATION: {
        const {proteinId, proteinAnnotationId} = action;
        if (this.uiStateByProteinId[proteinId].hoveredAnnotationId === proteinAnnotationId) {
          this._updateUiState(proteinId, {$merge: {hoveredAnnotationId: null}});
          this.emitChange();
        }
        break;
      }
      case EventConstants.PROTEIN_SELECT_ANNOTATION: {
        const {proteinId, proteinAnnotationId} = action;
        // Protein annotation id can be null
        this._updateUiState(proteinId, {$merge: {selectedAnnotationId: proteinAnnotationId}});
        const annotation = this.getAnnotation(proteinId, proteinAnnotationId);
        if (annotation != null) {
          const {name, type, start, color, notes} = annotation;
          let {end} = annotation;
          if (end === 0) {
            end = this.proteinsById[proteinId].version.aminoAcids.length;
          }
          const {displayStart, displayEnd} = this._getDisplaySelectionRange(proteinId, start, end);
          this._updateUiState(proteinId, {
            $merge: {
              pendingJump: {start, end},
              selectionStart: start,
              selectionEnd: end,
              annotationEditorData: {
                pendingEditorFocus: false,
                name,
                type,
                displayStart,
                displayEnd,
                color,
                notes,
              },
            },
          });
          this._recomputeAnnotationInputValidity(proteinId);
          this.emitChange();
        }
        break;
      }
      case EventConstants.PROTEIN_SELECT_RANGE: {
        const {proteinId, startIndex, endIndex} = action;
        const {selectionStart, selectionEnd} = this._getUIState(proteinId);
        if (startIndex === selectionStart && endIndex === selectionEnd) {
          break;
        }
        if (this._getUIState(proteinId).annotationEditorData) {
          const {displayStart, displayEnd} = this._getDisplaySelectionRange(proteinId, startIndex, endIndex);
          // If an annotation is already selected, then manually making a range selection should
          // deselect that annotation and close the editor. If we're creating a new annotation, sync
          // the selection.
          if (this._getUIState(proteinId).selectedAnnotationId != null) {
            this._updateAnnotationEditor(proteinId, {$set: null});
          } else {
            this._updateAnnotationEditor(proteinId, {$merge: {displayStart, displayEnd}});
          }
        }
        this._updateUiState(proteinId, {
          $merge: {
            selectedAnnotationId: null,
            selectionStart: startIndex,
            selectionEnd: endIndex,
          },
        });
        this.emitChange();
        break;
      }
      case EventConstants.FILE_BROWSER_RENAME_FILE: {
        const {id, name, displayLabel} = action;
        const protein = this.getProteinById(id);
        if (protein) {
          const newProtein = reactUpdate(protein, {$merge: {name, displayLabel}});
          this._setProtein(id, newProtein);
          this.emitChange();
        }
        break;
      }
      case EventConstants.FILE_BROWSER_MOVE_FILES_COMPLETED: {
        const {ids, folder} = action;
        let hasChanged = false;
        // eslint-disable-next-line no-restricted-syntax
        _.each(ids, (id: string) => {
          const protein = this.getProteinById(id);
          if (protein) {
            let newProtein = protein;
            if (RegistryUtil.isRegistry(folder.source)) {
              newProtein = reactUpdate(newProtein, {
                $merge: {projectFolderId: protein.folder.api_identifier},
              });
            }
            newProtein = reactUpdate(newProtein, {$merge: {folder}});
            this._setProtein(id, newProtein);
            hasChanged = true;
          }
        });
        if (hasChanged) {
          this.emitChange();
        }
        break;
      }
      case EventConstants.FILE_BROWSER_MOVE_SHORTCUTS_COMPLETED: {
        const {ids, folder} = action;
        let hasChanged = false;
        // eslint-disable-next-line no-restricted-syntax
        _.each(ids, (id: string) => {
          const protein = this.getProteinById(id);
          if (protein) {
            let newProtein = protein;
            newProtein = reactUpdate(newProtein, {
              $merge: {projectFolderId: folder && folder.api_identifier},
            });
            this._setProtein(id, newProtein);
            hasChanged = true;
          }
        });
        if (hasChanged) {
          this.emitChange();
        }
        break;
      }
      case EventConstants.FILE_BROWSER_UNREGISTER_FILES: {
        const {ids} = action;
        let hasChanged = false;
        // eslint-disable-next-line no-restricted-syntax
        _.each(ids, (id: string) => {
          const protein = this.getProteinById(id);
          if (protein) {
            // @ts-expect-error: should probably use undefined
            const newProtein = reactUpdate(protein, {$merge: {projectFolderId: null}});
            this._setProtein(id, newProtein);
            hasChanged = true;
          }
        });
        if (hasChanged) {
          this.emitChange();
        }
        break;
      }
      case EventConstants.PROTEIN_ANNOTATION_EDITOR_NEW_ANNOTATION: {
        const {proteinId} = action;
        const {selectionStart, selectionEnd} = this._getUIState(proteinId);
        const {displayStart, displayEnd} = this._getDisplaySelectionRange(
          proteinId,
          selectionStart,
          selectionEnd
        );
        this._updateUiState(proteinId, {
          $merge: {
            selectedAnnotationId: null,
            annotationEditorData: {
              // Signal to the editor that, at the next convenient opportunity, it should put
              // keyboard focus on the first edit field.
              pendingEditorFocus: true,
              name: '',
              type: '',
              displayStart,
              displayEnd,
              color: colors.randomPastelColor(),
              notes: '',
            },
          },
        });
        this._recomputeAnnotationInputValidity(proteinId);
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_ANNOTATION_EDITOR_SET_NAME: {
        const {proteinId, name} = action;
        this._updateAnnotationEditor(proteinId, {$merge: {name}});
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_ANNOTATION_EDITOR_SET_TYPE: {
        const {proteinId, annotationType} = action;
        this._updateAnnotationEditor(proteinId, {$merge: {type: annotationType}});
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_ANNOTATION_EDITOR_SET_NOTES: {
        const {proteinId, annotationNotes} = action;
        this._updateAnnotationEditor(proteinId, {$merge: {notes: annotationNotes}});
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_ANNOTATION_EDITOR_SET_DISPLAY_START: {
        const {proteinId, displayStart} = action;
        this._updateAnnotationEditor(proteinId, {$merge: {displayStart}});
        this.emitChange();
        break;
      }

      case EventConstants.PROTEIN_ANNOTATION_EDITOR_SET_DISPLAY_END: {
        const {proteinId, displayEnd} = action;
        this._updateAnnotationEditor(proteinId, {$merge: {displayEnd}});
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_ANNOTATION_EDITOR_SET_COLOR: {
        const {proteinId, color} = action;
        this._updateAnnotationEditor(proteinId, {$merge: {color}});
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_ANNOTATION_EDITOR_INITIAL_SAVE: {
        const {proteinId} = action;
        // For now, dismiss the editor immediately so it feels responsive and to avoid
        // double-clicking the button.
        this._updateAnnotationEditor(proteinId, {$set: null});
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_ANNOTATION_EDITOR_INITIAL_DELETE: {
        const {proteinId, annotationId} = action;
        if (annotationId === this.uiStateByProteinId[proteinId].selectedAnnotationId) {
          this._deselectAnnotation(proteinId);
          this.emitChange();
        }
        break;
      }
      case EventConstants.PROTEIN_ANNOTATION_EDITOR_CANCEL: {
        const {proteinId} = action;
        this._deselectAnnotation(proteinId);
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_PENDING_JUMP_COMPLETED: {
        const {proteinId} = action;
        this._updateUiState(proteinId, {$merge: {pendingJump: null}});
        this.emitChange();
        break;
      }
      case EventConstants.PROTEIN_ANNOTATION_EDITOR_PENDING_FOCUS_COMPLETED: {
        const {proteinId} = action;
        this._updateAnnotationEditor(proteinId, {$merge: {pendingEditorFocus: false}});
        this.emitChange();
        break;
      }
      default:
        break;
    }

    return true;
  }
}

const proteinStore = new ProteinStore(AppDispatcher);
export default proteinStore;
